// URL helpers + jotai mutation atoms for the structured-addresses
// endpoints. Each mutation goes through `createMutationAtom` so loading,
// error, and success states are managed centrally and a Rollbar trace is
// captured on fatal exceptions. Components dispatch via `[state, trigger] =
// useAtom(atom)` and pass `onSuccess` / `onError` for post-mutation UI.

import { atom } from 'jotai';
import { createMutationAtom } from 'atoms/create-resource-atom';
import { StructuredAddressActionResponse } from 'types/api-responses';

// Bumped after every successful structuring mutation so that read-only views
// (currently the document-level audit log) refetch without waiting for the
// next page load. Polling reloads of the parties list intentionally do NOT
// bump this — only direct user actions should trigger an audit-log refetch.
export const auditLogVersionAtom = atom(0);

// ---------------------------------------------------------------------------
// URL helpers
// ---------------------------------------------------------------------------

export const documentAddressesBaseUrl = (documentId: number) =>
  `/documents/${documentId}/structured_addresses`;

export const documentAddressUrl = (documentId: number, addressId: number) =>
  `${documentAddressesBaseUrl(documentId)}/${addressId}`;

// Move actions live on the doc-scoped structured_addresses resource. The
// owning party is derived server-side from the address's entity, so no
// party id is needed in the URL.
export const moveAddressToDocumentUrl = (
  documentId: number,
  addressId: number
) =>
  `/documents/${documentId}/structured_addresses/${addressId}/move_to_document`;

export const moveAddressToPartyUrl = (documentId: number, addressId: number) =>
  `/documents/${documentId}/structured_addresses/${addressId}/move_to_party`;

export const resolveToPartyUrl = (documentId: number, addressId: number) =>
  `${documentAddressUrl(documentId, addressId)}/resolve_to_party`;

export const clearAddressFlagUrl = (documentId: number, addressId: number) =>
  `${documentAddressUrl(documentId, addressId)}/clear_flag`;

export const markAddressCorrectUrl = (documentId: number, addressId: number) =>
  `${documentAddressUrl(documentId, addressId)}/mark_correct`;

export const restoreAddressUrl = (documentId: number, addressId: number) =>
  `${documentAddressUrl(documentId, addressId)}/restore`;

// ---------------------------------------------------------------------------
// Mutation atoms
// ---------------------------------------------------------------------------

export const createDocumentAddressAtom = createMutationAtom<
  StructuredAddressActionResponse,
  'structuredAddressCreate'
>();

export const updateDocumentAddressAtom = createMutationAtom<
  StructuredAddressActionResponse,
  'structuredAddressUpdate'
>();

export const deleteDocumentAddressAtom = createMutationAtom<
  StructuredAddressActionResponse,
  'structuredAddressRemoval'
>();

export const moveAddressToDocumentAtom = createMutationAtom<
  StructuredAddressActionResponse,
  'structuredAddressMoveToDocument'
>();

export const moveAddressToPartyAtom = createMutationAtom<
  StructuredAddressActionResponse,
  'structuredAddressMoveToParty'
>();

export const resolveAddressToPartyAtom = createMutationAtom<
  StructuredAddressActionResponse,
  'structuredAddressResolveToParty'
>();

export const clearAddressFlagAtom = createMutationAtom<
  StructuredAddressActionResponse,
  'structuredAddressClearFlag'
>();

export const markAddressCorrectAtom = createMutationAtom<
  StructuredAddressActionResponse,
  'structuredAddressMarkCorrect'
>();

export const restoreAddressAtom = createMutationAtom<
  StructuredAddressActionResponse,
  'structuredAddressRestore'
>();
